import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import EditableCard from '../../EditableCard/EditableCard';
import { useUploadImageMutation, useUpdateKeyInformationMutation } from '../../drApi/drApi';
import { errorToast } from '../../toasts/toastsThunks';
import KeyImagePicker from '../KeyImagePicker';
import pickerStyles from '../KeyImagePicker.module.scss';

const KeyImageEditableCard = ({ keyInformation }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { _id: keyInformationId, image } = keyInformation;
  const [isEditing, setIsEditing] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [removeImage, setRemoveImage] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [uploadImage, { isLoading: isLoadingUpload }] = useUploadImageMutation();
  const [updateKeyInformation, { isLoading: isLoadingUpdate }] = useUpdateKeyInformationMutation();

  const resetState = useCallback(() => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setRemoveImage(false);
  }, []);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const imageUrl = useMemo(() => {
    if (removeImage) {
      return null;
    }
    return previewUrl || image || null;
  }, [image, previewUrl, removeImage]);

  const onFileSelected = useCallback((event) => {
    const file = event.currentTarget.files?.[0];
    if (!file) {
      return;
    }

    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    setRemoveImage(false);
  }, [previewUrl]);

  const onRemove = useCallback(() => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setSelectedFile(null);
    setPreviewUrl(null);
    setRemoveImage(true);
  }, [previewUrl]);

  const onAbort = useCallback(() => {
    resetState();
  }, [resetState]);

  const onSubmit = useCallback(async () => {
    if (isSaving) {
      return;
    }
    if (!selectedFile && !removeImage) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    let nextImage = null;
    try {
      if (selectedFile) {
        try {
          const response = await uploadImage(selectedFile);
          const { data, error } = response || {};
          if (error || !data) {
            dispatch(errorToast({ text: t('Upload fehlgeschlagen') + (error?.message ? `: ${error.message}` : '') }));
            return;
          }
          nextImage = data?.imageUrl;
        } catch {
          nextImage = false;
        }
        if (!nextImage) {
          dispatch(errorToast({ text: t('Upload fehlgeschlagen') }));
          return;
        }
      }

      await updateKeyInformation({
        keyInformationId,
        updateFieldsInformation: {
          image: removeImage ? null : nextImage,
        },
      }).unwrap();
      resetState();
      setIsEditing(false);
    } finally {
      setIsSaving(false);
    }
  }, [
    dispatch,
    uploadImage,
    isSaving,
    keyInformationId,
    removeImage,
    resetState,
    selectedFile,
    t,
    updateKeyInformation,
  ]);

  return (
    <EditableCard
      onEditingChange={setIsEditing}
      isEditing={isEditing}
      title={t('Bild')}
      className={pickerStyles.card}
      canDelete={false}
      canAbort
      isLoading={isSaving || isLoadingUpload || isLoadingUpdate}
      onAbort={onAbort}
      onAccept={onSubmit}
      large
    >
      <KeyImagePicker
        imageUrl={imageUrl}
        onFileSelected={onFileSelected}
        onRemove={onRemove}
        isEditing={isEditing}
        disabled={isSaving || isLoadingUpload || isLoadingUpdate}
      />
    </EditableCard>
  );
};

KeyImageEditableCard.propTypes = {
  keyInformation: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    image: PropTypes.string,
  }).isRequired,
};

export default KeyImageEditableCard;
